/**
 * Line, area, bar, scatter — and any mix of them — on one or two axes.
 * Which series goes where is decided in lookerChartData.cartesianSeries; how
 * it looks comes from ./chartTheme.
 *
 * Animation is off, as on the Dashboard trend: tiles reload on every filter
 * change, and re-growing from zero each time reads as the data flickering.
 *
 * @module components/looker/charts/CartesianTileChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar, Scatter, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
  ReferenceLine, ReferenceArea,
} from 'recharts';
import type { LookerTile } from '@/services/looker/lookerTypes';
import {
  cartesianSeries, chartRows, fillReferenceLabel, isIsoDate, referenceValue, type CartesianSeries, type ChartPoint,
} from '../lookerChartData';
import type { LookerReferenceLine } from '@/services/looker/lookerTypes';
import {
  AXIS_PROPS, CHART_HEIGHT, GRID_PROPS, GRID_PROPS_HORIZONTAL_BARS, LEGEND_PROPS, TOOLTIP_PROPS, VALUE_LABEL_PROPS,
  categoryTick, compactTick, fullNumber, percentTick, useSvgId,
} from './chartTheme';

interface DrawOptions {
  horizontal: boolean;
  labels: boolean;
  /** Prefix for this chart's gradient ids. */
  gradient: string;
  /**
   * Smooth only over time. Between categories (channels, campaigns) a curve
   * invents a peak between two bars that no data point holds.
   */
  curve: 'monotone' | 'linear';
  /** Few points: mark each one, or a three-point line hides where the data is. */
  dots: boolean;
}

/** Above this many points the dots crowd the line. */
const MAX_DOTTED_POINTS = 12;

const gradientId = (gradient: string, i: number) => `${gradient}-${i}`;

const renderSeries = (s: CartesianSeries, i: number, o: DrawOptions) => {
  const axisProps = o.horizontal ? {} : { yAxisId: s.axis };
  const label = o.labels
    ? <LabelList dataKey={s.name} position={o.horizontal ? 'right' : 'top'} formatter={compactTick} {...VALUE_LABEL_PROPS} />
    : null;
  // `key` goes on the element itself: React warns when a spread object carries it.
  const common = { ...axisProps, dataKey: s.name, name: s.label, isAnimationActive: false };
  switch (s.type) {
    case 'line':
      return (
        <Line key={s.name} {...common} type={o.curve} stroke={s.color} strokeWidth={2.5} dot={o.dots ? { r: 3, strokeWidth: 0, fill: s.color } : false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls={false}>
          {label}
        </Line>
      );
    case 'area':
      return (
        <Area
          key={s.name} {...common} type={o.curve} stroke={s.color} strokeWidth={2} fill={`url(#${gradientId(o.gradient, i)})`} fillOpacity={1}
          stackId={s.stackId} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls={false}
        >
          {label}
        </Area>
      );
    case 'scatter':
      return <Scatter key={s.name} {...common} fill={s.color} fillOpacity={0.85}>{label}</Scatter>;
    default: {
      // Only the outer end is rounded; inside a stack every segment would round and look beaded.
      const radius: [number, number, number, number] = s.stackId ? [0, 0, 0, 0] : o.horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0];
      return <Bar key={s.name} {...common} fill={s.color} stackId={s.stackId} radius={radius} maxBarSize={40}>{label}</Bar>;
    }
  }
};

const REFERENCE_GREY = '#5f6368';

/**
 * Looker draws reference lines against the first series; so does this, on
 * that series' axis. A line whose value cannot be worked out is not drawn.
 */
const renderReferences = (lines: LookerReferenceLine[], data: ChartPoint[], first: CartesianSeries | undefined, horizontal: boolean) => {
  if (!first) return [];
  const values = data.flatMap((p) => (typeof p[first.name] === 'number' ? [p[first.name] as number] : []));
  const axis = horizontal ? {} : { yAxisId: first.axis };
  return lines.flatMap((l, i) => {
    const a = referenceValue(l.value, values);
    const b = l.end === null ? null : referenceValue(l.end, values);
    if (a === null || (l.kind === 'range' && b === null)) return [];
    const color = l.color ?? REFERENCE_GREY;
    const text = fillReferenceLabel(l.label, values);
    // Bands label top-left and lines top-right, so a mean line inside a band does not print over it.
    const label = (position: 'insideTopLeft' | 'insideTopRight') =>
      (text ? { value: text, position, fill: '#3c4043', fontSize: 11 } : undefined);
    if (l.kind === 'range' && b !== null) {
      const edge = horizontal ? { x1: a, x2: b } : { y1: a, y2: b };
      return [<ReferenceArea key={`ref-${i}`} {...axis} {...edge} fill={color} fillOpacity={0.1} stroke="none" label={label('insideTopLeft')} ifOverflow="extendDomain" />];
    }
    const at = horizontal ? { x: a } : { y: a };
    return [<ReferenceLine key={`ref-${i}`} {...axis} {...at} stroke={color} strokeDasharray="4 4" strokeWidth={1.5} label={label('insideTopRight')} ifOverflow="extendDomain" />];
  });
};

export const CartesianTileChart: React.FC<{ tile: LookerTile }> = ({ tile }) => {
  const { t } = useTranslation();
  const gradient = useSvgId('looker-area');
  const { data } = chartRows(tile);
  const series = cartesianSeries(tile);
  // Sideways bars have one value axis; a right-hand axis there is not drawn.
  const horizontal = tile.vis.horizontal && tile.type === 'bar';
  const hasRight = !horizontal && series.some((s) => s.axis === 'right');
  const percent = tile.vis.stacking === 'percent';
  // Labels on a 100% stack would show raw values against a percent axis.
  const labels = tile.vis.valueLabels && !percent;
  const valueTick = percent ? percentTick : compactTick;
  const category = categoryTick(t);
  const curve = data.length > 0 && data.every((p) => isIsoDate(p.x)) ? 'monotone' : 'linear';
  const dots = data.length <= MAX_DOTTED_POINTS;

  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <ComposedChart
        data={data}
        layout={horizontal ? 'vertical' : 'horizontal'}
        stackOffset={percent ? 'expand' : undefined}
        margin={{ top: labels ? 20 : 8, right: horizontal && labels ? 40 : 8, bottom: 0, left: 0 }}
      >
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s.name} id={gradientId(gradient, i)} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.35} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0.03} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid {...(horizontal ? GRID_PROPS_HORIZONTAL_BARS : GRID_PROPS)} />
        {horizontal ? (
          <>
            <XAxis type="number" {...AXIS_PROPS} tickFormatter={valueTick} />
            <YAxis type="category" dataKey="x" {...AXIS_PROPS} tickFormatter={category} width={110} />
          </>
        ) : (
          <>
            <XAxis dataKey="x" {...AXIS_PROPS} tickFormatter={category} minTickGap={16} />
            <YAxis yAxisId="left" {...AXIS_PROPS} tickFormatter={valueTick} width={48} />
            {hasRight && <YAxis yAxisId="right" orientation="right" {...AXIS_PROPS} tickFormatter={compactTick} width={48} />}
          </>
        )}
        <Tooltip {...TOOLTIP_PROPS} formatter={fullNumber} labelFormatter={category} />
        {series.length > 1 && <Legend {...LEGEND_PROPS} />}
        {series.map((s, i) => renderSeries(s, i, { horizontal, labels, gradient, curve, dots }))}
        {/* A 100% stack has no value axis to hang a line on. */}
        {!percent && renderReferences(tile.vis.referenceLines, data, series[0], horizontal)}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
